










import Enumerable from "linq";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { ModelEditableMixin } from "../../../../../base/components/system";
import { ILinkedObjectSpecifiable, ILinkedPageObjectSpecifiable, IObjectsControllable, TextObjectKinds, TranscribeTargetKinds } from "../../../../domain/format/objects";
import { CommonComponentsContainer } from "../../../../../formatEditor/components/format/editors/contents";
import { IFormatObjectSelectFilterParameter } from "../../../../../formatEditor/components/format/editors/contents/commonComponents/dialogs";
import { FormatObjectSelectorColumnTypes } from "../../../../../formatEditor/components/format/editors/contents/commonComponents/selectors";
import { IFormatObjectEditable } from "../../../../../formatEditor/domain/editableFormat";
import { TextObjectEditor, TagObjectEditor, CheckObjectEditor, CircleSelectObjectEditor } from "../../../../../formatEditor/domain/editableFormat/objectEditors";

@Component
export default class TranscribeBehaviorEditor extends Mixins(ModelEditableMixin) {
  // ModelEditableMixinと同時に使う事.ModelEditableMixin利用APIの宣言.
  writePropertyWhenValid: (writer: (source: IObjectsControllable<ILinkedPageObjectSpecifiable>) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: IObjectsControllable<ILinkedPageObjectSpecifiable>) => T, defaultValue: T) => T;

  @Prop()
  selectableTargets: TranscribeTargetKinds[];

  @Prop()
  commonComponents: CommonComponentsContainer;

  get selectedObjectTypeName(): string {
    return Enumerable.from(this.commonComponents.currentPage.getSelectedObjects())
      .where(o => !!o.isSelected)
      .select(s => s.objectTypeName().getValue())
      .firstOrDefault();
  }

  get pageKey(): number {
    return this.commonComponents.currentPage.pageKey().getValue();
  }

  get targets(): ILinkedPageObjectSpecifiable[] {
    return this.readPropertyOrDefault(s => s.targets, []);
  }

  set targets(value: ILinkedPageObjectSpecifiable[]) {
    this.writePropertyWhenValid(s => s.targets = value);
  }

  private compareLinkedPageObjectSpecifiable(a: ILinkedPageObjectSpecifiable, b: ILinkedPageObjectSpecifiable): boolean {
    return a.pageKey === b.pageKey &&
      a.type === b.type &&
      a.key === b.key &&
      a.subKey === b.subKey &&
      (a.tableIndex ? a.tableIndex.row : null) === (b.tableIndex ? b.tableIndex.row : null) &&
      (a.tableIndex ? a.tableIndex.column : null) === (b.tableIndex ? b.tableIndex.column : null) &&
      a.isDate === b.isDate;
  }

  async showTranscribeEditDialog() {
    const filters = this.getFilters();
    const dialog = this.commonComponents.getDialogs().getFormatObjectSelectDialog();
    const targets = await dialog.show(
      this.targets,
      Enumerable.from(this.getSelectableObjects()).where(o => Enumerable.from(filters).any(f => f.filter(o))).toArray(),
      ["selection", "type", "key", "subKey", "id", "pageNumber"],
      ["selection", "type", "key", "subKey", "id", "pageNumber"],
      false,
      false,
      filters,
      this.toCellString);
    if (!targets) { return; }
    this.targets = targets;
  }

  private getFilters(): IFormatObjectSelectFilterParameter[] {
    return Enumerable.from(this.selectableTargets).select(t => this.toFilter(t)).toArray();
  }

  private toFilter(kind: TranscribeTargetKinds): IFormatObjectSelectFilterParameter {
    switch (kind) {
      case TranscribeTargetKinds.FreeText:
        return {
          enabled: false,
          name: "フリー入力",
          description: "フリー入力テキストで絞り込みます",
          filter: v => v.type === "text",
        };
      case TranscribeTargetKinds.Text:
        return {
          enabled: false,
          name: "テキスト",
          description: "テキストで絞り込みます",
          filter: v => v.type === "tag",
        };
      case TranscribeTargetKinds.Check:
        return {
          enabled: false,
          name: "チェックボックス",
          description: "チェックボックスで絞り込みます",
          filter: v => v.type === "check",
        };
      case TranscribeTargetKinds.CircleSelect:
        return {
          enabled: false,
          name: "円選択",
          description: "円選択で絞り込みます",
          filter: v => v.type === "circleselect",
        };
    }
  }

  private getSelectableObjects(): ILinkedPageObjectSpecifiable[] {
    return Enumerable.from(this.commonComponents.allPagesFormatObjectsForEachPage)
      .selectMany(p => Enumerable.from(p.objects)
        .where(o => this.isSelectableObject(o))
        .select(o => this.toLinkedPageObjectSpecifiable(o, p.pageKey))
        .toArray())
      .toArray();
  }

  private isSelectableObject(object: IFormatObjectEditable): boolean {
    return !object.isSelected()
        && (this.isSelectableTextObject(object) || this.isSelectableTagObject(object) || this.isSelectableCheckObject(object) || this.isSelectableCircleSelectObject(object));
  }

  private isSelectableTextObject(object: IFormatObjectEditable) {
    return (object instanceof TextObjectEditor)
        && (object.objectKind().getValue() === TextObjectKinds.Free)
        && this.isLinkableObjectIdentifier(object.toLinkedObjectIdentifier());
  }

  private isSelectableTagObject(object: IFormatObjectEditable) {
    return (object instanceof TagObjectEditor)
        && this.isLinkableObjectIdentifier(object.toLinkedObjectIdentifier());
  }

  private isSelectableCheckObject(object: IFormatObjectEditable) {
    return (object instanceof CheckObjectEditor)
        && this.isLinkableCheckableObjectIdentifier(object.toLinkedObjectIdentifier());
  }

  private isSelectableCircleSelectObject(object: IFormatObjectEditable) {
    return (object instanceof CircleSelectObjectEditor)
        && this.isLinkableCheckableObjectIdentifier(object.toLinkedObjectIdentifier());
  }

  private isLinkableObjectIdentifier(object: ILinkedObjectSpecifiable): boolean {
    return !!object.id
        && !!object.key
        && (object.key !== "");
  }

  private isLinkableCheckableObjectIdentifier(object: ILinkedObjectSpecifiable): boolean {
    return this.isLinkableObjectIdentifier(object)
        && !!object.subKey
        && (object.subKey !== "");
  }

  private toLinkedPageObjectSpecifiable(object: IFormatObjectEditable, pageKey: number): ILinkedPageObjectSpecifiable {
    const linkedObject = object.toLinkedObjectIdentifier();
    return {
      pageKey: pageKey,
      type: linkedObject.type,
      id: linkedObject.id,
      key: linkedObject.key,
      subKey: linkedObject.subKey,
      tableIndex: linkedObject.tableIndex,
      isDate: linkedObject.isDate,
    };
  }

  private toCellString(column: FormatObjectSelectorColumnTypes, row: ILinkedPageObjectSpecifiable): string {
    const formatObject = this.getFormatObjectEditable(row);
    switch (column) {
      case "type":
        return formatObject ? formatObject.getTypeName() + (!!row.tableIndex ? "[" + row.tableIndex.row + "][" + row.tableIndex.column + "]" : "") : "";
      case "id":
        return row.id ? row.id.toString() : "";
      case "key":
        return row.key; // formatObject.getIdentifiableName()を出したほうが良いと思われるが要検討
      case "subKey":
        return row.subKey ? row.subKey : "";
      case "pageNumber":
        const pageNumber = this.commonComponents.getPageNumber(row.pageKey);
        return pageNumber === 0 ? "削除済" : pageNumber.toString();
      default:
        return "";
    }
  }

  private getFormatObjectEditable(row: ILinkedPageObjectSpecifiable): IFormatObjectEditable {
    const pageFormatObjects = this.commonComponents.allPagesFormatObjectsForEachPage.find(p => p.pageKey === row.pageKey);
    if (!pageFormatObjects) {
      return null;
    }
    return Enumerable.from(pageFormatObjects.objects)
      .firstOrDefault(o => this.compareLinkedPageObjectSpecifiable(this.toLinkedPageObjectSpecifiable(o, pageFormatObjects.pageKey), row));
  }
}
